<script setup>
// core
import {ref, nextTick} from "vue";

// components
import BaseCopyButton from "@/components/ui/BaseCopyButton.vue";

// constants
import {SIP_SERVER} from "@/components/Telephony/constants";

const isExpanded = ref(false)
const isBottom = ref(false)
const body = ref(null)

// the fade at the foot of the steps only makes sense while something is left
// below it
const syncFade = () => {
    const el = body.value
    if (!el) return

    isBottom.value = el.scrollTop + el.clientHeight >= el.scrollHeight - 1
}

const toggle = async () => {
    isExpanded.value = !isExpanded.value

    await nextTick()
    syncFade()
}
</script>

<template>
    <div class="sip-server" :class="{ 'is-collapsed': !isExpanded, 'is-bottom': isBottom }">
        <div class="sip-server__head">
            <div class="sip-server__left">
                <span class="sip-server__label item_title">{{ $t('telephony.sip.server') }}</span>
                <span class="sip-server__pair">
                    <span class="sip-server__value">{{ SIP_SERVER }}</span>
                    <BaseCopyButton
                        :value="SIP_SERVER"
                        :title="$t('telephony.sip.copy_server')"
                        toast="telephony.sip.copied"
                    />
                </span>
            </div>

            <button
                type="button"
                class="sip-server__toggle"
                @click="toggle"
            >
                {{ $t('telephony.sip.how_to_setup') }}
                <span class="sip-server__arrow">
                    <i class="icon-chevron-down sip-server__chevron"></i>
                </span>
            </button>
        </div>

        <div class="sip-server__body" ref="body" @scroll="syncFade" @transitionend="syncFade">
            <p class="sip-server__lead">{{ $t('telephony.sip.setup_lead') }}</p>

            <ol class="sip-server__steps">
                <li v-for="(step, i) in $tm('telephony.sip.setup_steps')" :key="i">
                    <span class="sip-server__num">{{ i + 1 }}</span>
                    <span v-html="step"></span>
                </li>
            </ol>
        </div>
    </div>
</template>

<style scoped lang="scss">
// .setting_block skin from SettingsNumberDialog
.sip-server {
    position: relative;
    font-family: 'Gilroy', sans-serif;
    padding: 12px 15px;
    border-radius: 7px;
    border: 1px solid var(--general-color);
    background-color: var(--block-color);

    display: flex;
    flex-direction: column;
    // no gap: the spacing lives in the list margin so it collapses with it
    gap: 0;

    // the open block gives way before the trunk list does, but never below the
    // head plus a couple of steps
    flex: 0 1 auto;
    overflow: hidden;

    &__head {
        display: flex;
        align-items: center;
        gap: 16px;
        flex-wrap: wrap;
    }

    &__left {
        display: flex;
        align-items: center;
        gap: 10px;
        margin-right: auto;
    }

    // the address and its copy button only ever wrap together
    &__pair {
        display: flex;
        align-items: center;
        gap: 10px;
        min-width: 0;
    }

    // same as the field labels in SipExtensionCard
    &__label {
        display: flex;
        text-transform: uppercase;
        font-family: 'Gilroy', sans-serif;
        font-size: 12px;
        font-weight: 500;
        letter-spacing: 0.02em;
        color: var(--border-primary-color);

        &::after {
            content: ":";
        }
    }

    &__value {
        color: var(--text-color);
        font-size: 16px;
        font-weight: 500;
        letter-spacing: 0.05rem;
    }

    &__toggle {
        display: inline-flex;
        align-items: center;
        gap: 8px;

        // reset the global button rule from table.scss
        width: auto;
        max-width: none;
        min-width: 0;
        padding: 0;
        border: none;
        border-radius: 0;
        background: none;
        cursor: pointer;

        font-family: 'Gilroy', sans-serif;
        font-weight: 500;
        font-size: 12px;
        letter-spacing: 0.02em;
        text-transform: uppercase;
        line-height: 24px;
        color: var(--border-primary-color);

        &:hover {
            color: var(--general-color);
        }
    }

    // a bare chevron in the colour of the label, like the number rows of
    // ConnectedNumbersMobileTable
    &__arrow {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        line-height: 1;
    }

    &__chevron {
        display: block;
        transition: transform 0.3s ease;
        color: inherit;
        font-size: 12px;
        transform: rotate(180deg);
    }

    &__body {
        margin: 12px 0 0;
        padding: 12px 0 0;
        border-top: 1px solid #cdcaf3;

        // same as .slide in ConnectedNumbersMobileTable
        overflow: hidden;
        max-height: 600px;
        opacity: 1;
        transition: all 0.3s ease-out;
    }

    // the steps scroll inside the block instead of pushing the trunk list out
    // of the dialog
    &:not(.is-collapsed) {
        min-height: 220px;

        .sip-server__body {
            // a flex item defaults to min-height: auto, which lets the steps
            // spill out from under the border instead of scrolling
            min-height: 0;
            max-height: 300px;
            overflow-y: auto;

            padding-right: 10px;
            margin-right: -10px;

            &::-webkit-scrollbar {
                width: 3px;
            }

            &::-webkit-scrollbar-track {
                background: var(--block-color);
            }

            &::-webkit-scrollbar-thumb {
                background: #7970dfbf;
                border-radius: 3px;
            }

            &::-webkit-scrollbar-thumb:hover {
                background: var(--general-color);
            }
        }
    }

    // the fade says the steps continue; .is-bottom drops it once they do not
    &:not(.is-collapsed):not(.is-bottom)::after {
        content: "";
        position: absolute;
        left: 1px;
        right: 1px;
        bottom: 1px;
        height: 26px;

        border-radius: 0 0 7px 7px;
        background: linear-gradient(to bottom, rgba(244, 243, 252, 0), var(--block-color));
        pointer-events: none;
        transition: opacity 0.2s;
    }

    &__lead {
        margin: 0 0 12px;
        font-family: 'Gilroy', sans-serif;
        font-size: 15px;
        font-weight: 500;
        line-height: 1.6;
        color: var(--text-third-color);
    }

    &__steps {
        list-style: none;
        margin: 0;
        padding: 0;

        display: flex;
        flex-direction: column;
        gap: 10px;

        li {
            display: flex;
            align-items: flex-start;
            gap: 12px;

            font-family: 'Gilroy', sans-serif;
            font-size: 15px;
            line-height: 1.6;
            color: var(--text-third-color);
        }

        b {
            font-weight: 700;
            color: var(--text-color);
        }
    }

    // .number-settings__block-count
    &__num {
        flex: 0 0 auto;
        width: 26px;
        height: 26px;

        display: inline-flex;
        align-items: center;
        justify-content: center;

        border-radius: 7px;
        background-color: #cdcaf3;
        color: var(--general-color);
        font-family: 'Gilroy', sans-serif;
        font-size: 13px;
        font-weight: 700;
    }

    &.is-collapsed {
        // overflow: hidden drops the automatic min-height, so a short dialog
        // would squeeze the closed head down to a sliver
        flex-shrink: 0;

        .sip-server__chevron {
            transform: rotate(0);
        }

        .sip-server__body {
            max-height: 0;
            margin-top: 0;
            padding-top: 0;
            opacity: 0;
            border-top-color: transparent;
        }
    }
}

// breakpoints match SettingsNumberDialog
@media (max-width: 570px) {
    .sip-server {
        // the toggle takes a row of its own and reads as an accordion header:
        // label on the left, arrow on the right, rule above
        &__head {
            flex-direction: column;
            align-items: stretch;
            gap: 10px;
        }

        &__left {
            flex-wrap: wrap;
            row-gap: 2px;
            margin-right: 0;
        }

        &__toggle {
            width: 100%;
            justify-content: space-between;
            padding-top: 10px;
            border-top: 1px solid #cdcaf3;
        }

        // the toggle already draws the rule — the body does not need a second one
        &__body {
            margin-top: 0;
            border-top: none;
        }
    }
}
</style>
